// Guards the PY-1104 regression: the registry calls went out with no bearer
// token and inventory-service answered 401 to all of them. Both axios
// instances must attach a token, because interceptors are per instance.

const GATEWAY_URI = "https://api.example.test/data/";
const INVENTORY_URI = "https://inventory.example.test";

let mockCurrentToken;
let mockLogout;

jest.mock("./Auth0TokenService", () => ({
  getTokenContext: () => ({
    getAccessTokenSilently: jest.fn(async (options) => {
      if (options?.cacheMode === "off") {
        mockCurrentToken = "fresh-token";
      }
      return mockCurrentToken;
    }),
    logout: mockLogout,
  }),
}));

// Records what each instance would have put on the wire, and answers with
// whatever the test asks for instead of making a real request.
const captureAdapter = (client, responder) => {
  const sent = [];

  client.defaults.adapter = (config) => {
    sent.push(config);
    return responder(config, sent.length);
  };

  return sent;
};

const ok = (config) =>
  Promise.resolve({
    data: { data: [], "page-details": { "total-rows": 0 } },
    status: 200,
    statusText: "OK",
    headers: {},
    config,
  });

const unauthorized = (config) => {
  const error = new Error("Request failed with status code 401");
  error.config = config;
  error.response = { status: 401, data: {}, headers: {}, config };
  return Promise.reject(error);
};

let Auth0Axios;
let InventoryService;
let HoldingService;

beforeEach(() => {
  jest.resetModules();

  mockCurrentToken = "stale-token";
  mockLogout = jest.fn(async () => {});

  window.env = {
    REACT_APP_BASE_URI: GATEWAY_URI,
    INVENTORY_API_BASE_URI: INVENTORY_URI,
  };

  Auth0Axios = require("./Auth0Axios");
  InventoryService = require("./InventoryService");
  HoldingService = require("./HoldingService");

  Auth0Axios.initializeAuth0Axios();
});

describe("registry calls", () => {
  it("carry a bearer token", async () => {
    const sent = captureAdapter(Auth0Axios.inventoryClient, ok);

    await InventoryService.getRetirementHistory();

    expect(sent).toHaveLength(1);
    expect(sent[0].headers.Authorization).toBe("Bearer stale-token");
    expect(sent[0].url).toContain("/data/registry/retirement-history");
  });

  it("carry a bearer token on every endpoint the portal calls", async () => {
    const sent = captureAdapter(Auth0Axios.inventoryClient, ok);

    await InventoryService.getInventoryData();
    await InventoryService.getRegistryData();
    await InventoryService.getStatusData();
    await InventoryService.getAssets();
    await InventoryService.getChains();
    await InventoryService.getRetirementRequests();
    await InventoryService.getCertificateDocuments();
    await InventoryService.getTransactions();
    await InventoryService.updateInventoryItem({ id: 1 });
    await InventoryService.createCertificate(1);
    await InventoryService.createRetirementHistory({});
    await InventoryService.getCertificateDocument(1);

    expect(sent).toHaveLength(12);
    sent.forEach((request) => {
      expect(request.headers.Authorization).toBe("Bearer stale-token");
    });
  });

  it("retry once with a refreshed token after a 401", async () => {
    const sent = captureAdapter(Auth0Axios.inventoryClient, (config, call) =>
      call === 1 ? unauthorized(config) : ok(config)
    );

    const result = await InventoryService.getRetirementHistory();

    expect(sent).toHaveLength(2);
    expect(sent[1].headers.Authorization).toBe("Bearer fresh-token");
    expect(result.status).toBe(true);
    expect(mockLogout).not.toHaveBeenCalled();
  });
});

describe("gateway calls", () => {
  it("carry a bearer token", async () => {
    const sent = captureAdapter(Auth0Axios.apiClient, ok);

    await HoldingService.getGroups();

    expect(sent).toHaveLength(1);
    expect(sent[0].headers.Authorization).toBe("Bearer stale-token");
  });

  it("go to the gateway, not to inventory-service", async () => {
    const sent = captureAdapter(Auth0Axios.apiClient, ok);

    await HoldingService.getGroups();

    expect(sent[0].url).toContain(GATEWAY_URI);
    expect(sent[0].url).not.toContain(INVENTORY_URI);
  });
});
